import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import DogForm from '../components/DogForm';
import DogsDisplay from '../components/DogsDisplay';
import DogCard from '../components/DogCard';
import DogDetailsModal from '../components/DogDetailsModal';
import OwnerForm from '../components/OwnerForm';
import { useDogs } from '../hooks/useDogs';
import '../styles/pages/dogs.css';

// ── Avatar palette (breed → pastel bg + emoji) ──────────────────────────────
const BREED_PALETTE = [
    { test: b => b.includes('pomer'),                           bg: '#FFF0F6', emoji: '🐩' },
    { test: b => b.includes('french') || b.includes('bulldog'), bg: '#FFF5E6', emoji: '🐶' },
    { test: b => b.includes('labrador') || b.includes(' lab'),  bg: '#EDFAF4', emoji: '🐕' },
    { test: b => b.includes('golden'),                          bg: '#FFF5E6', emoji: '🐶' },
    { test: b => b.includes('collie') || b.includes('border'),  bg: '#FFD8E5', emoji: '🐩' },
    { test: b => b.includes('husky'),                           bg: '#D8E8FF', emoji: '🐕‍🦺' },
    { test: b => b.includes('boxer'),                           bg: '#D9F2DD', emoji: '🐕‍🦺' },
    { test: b => b.includes('poodle'),                          bg: '#F2E2FF', emoji: '🐩' },
    { test: b => b.includes('beagle'),                          bg: '#FFE5C8', emoji: '🐶' },
];

const getDogStyle = (breed) => {
    const b = (breed || '').toLowerCase();
    return BREED_PALETTE.find(s => s.test(b)) || { bg: '#EFE6FF', emoji: '🐕' };
};

const getAgeShort = (dog) => {
    if (dog.calculatedAge?.displayText) {
        const t = dog.calculatedAge.displayText;
        const y = t.match(/^(\d+)\s+year/);
        if (y) return `${y[1]}y`;
        const m = t.match(/^(\d+)\s+month/);
        if (m) return `${m[1]}mo`;
    }
    if (dog.age) return `${dog.age}y`;
    return '';
};

// ── Filter chip definitions ──────────────────────────────────────────────────
const CHIPS = [
    { id: 'all',     label: 'Todos'     },
    { id: 'daycare', label: 'Guardería' },
    { id: 'hotel',   label: 'Hotel'     },
    { id: 'sched',   label: 'Programado' },
];

// ── Status pill helper ───────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
    if (!status) return null;
    const map = {
        in:        { label: 'Entrada',    cls: 'dmv-pill--in'    },
        daycare:   { label: 'Guardería',  cls: 'dmv-pill--in'    },
        hotel:     { label: 'Hotel',      cls: 'dmv-pill--hotel' },
        scheduled: { label: 'Programado', cls: 'dmv-pill--sched' },
        sched:     { label: 'Programado', cls: 'dmv-pill--sched' },
        grooming:  { label: 'Estética',   cls: 'dmv-pill--groom' },
        groom:     { label: 'Estética',   cls: 'dmv-pill--groom' },
    };
    const p = map[status];
    if (!p) return null;
    return <span className={`dmv-pill ${p.cls}`}>{p.label}</span>;
};

const PaginationControls = ({ page, totalPages, setPage }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="dogs-pagination">
            <button
                className="dogs-page-btn"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
            >
                ‹ Anterior
            </button>
            <span className="dogs-page-info">
                Página <strong>{page}</strong> de <strong>{totalPages}</strong>
            </span>
            <button
                className="dogs-page-btn"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
            >
                Siguiente ›
            </button>
        </div>
    );
};

// ────────────────────────────────────────────────────────────────────────────
const Dogs = () => {
    const { dogs, loading, error, createDog, updateDog, deleteDog, patchDog, clearError, page, setPage, totalPages, totalDogs, loadDogs } = useDogs();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // shared state
    const [activeTab,        setActiveTab]        = useState(searchParams.get('tab') || 'all-dogs');
    const [editingDog,       setEditingDog]        = useState(null);
    const [message,          setMessage]           = useState({ type: '', text: '' });
    const [searchTerm,       setSearchTerm]        = useState('');
    const [breedFilter,      setBreedFilter]       = useState('');
    const [overviewSearch,   setOverviewSearch]    = useState('');
    const [selectedDog,      setSelectedDog]       = useState(null);
    const [isModalOpen,      setIsModalOpen]       = useState(false);
    const [editingOwner,     setEditingOwner]      = useState(null);
    const [mobileFilter,     setMobileFilter]      = useState('all');

    // auto-clear messages
    useEffect(() => {
        if (!message.text) return;
        const t = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        return () => clearTimeout(t);
    }, [message]);

    // Backend pagination trigger
    useEffect(() => {
        const timer = setTimeout(() => {
            const query = activeTab === 'overview' ? overviewSearch : searchTerm;
            const params = { page, limit: 20, search: query };
            if (activeTab === 'all-dogs' && breedFilter) {
                params.breed = breedFilter;
            }
            loadDogs(params);
        }, 300);
        return () => clearTimeout(timer);
    }, [page, searchTerm, overviewSearch, activeTab, breedFilter, loadDogs]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, overviewSearch, activeTab, breedFilter, setPage]);

    // ── CRUD handlers ────────────────────────────────────────────────────────
    const handleFormSubmit = async (formData) => {
        try {
            clearError();
            if (editingDog) {
                await updateDog(editingDog._id, formData);
                setMessage({ type: 'success', text: '¡Perro actualizado con éxito!' });
            } else {
                await createDog(formData);
                setMessage({ type: 'success', text: '¡Perro agregado con éxito!' });
            }
            setEditingDog(null);
            setActiveTab('all-dogs');
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleEdit = (dog) => { setEditingDog(dog); setActiveTab('dog-form'); };

    const handleDelete = async (dogId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este perro?')) return;
        try {
            clearError();
            await deleteDog(dogId);
            setMessage({ type: 'success', text: '¡Perro eliminado con éxito!' });
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        }
    };

    const handleFormCancel = () => { setEditingDog(null); setActiveTab('all-dogs'); };

    const handleViewDetails = (dog) => { setSelectedDog(dog); setIsModalOpen(true); };
    const handleCloseModal  = ()    => { setIsModalOpen(false); setSelectedDog(null); };

    const handleOwnerFormSubmit = (newOwner) => {
        setMessage({ type: 'success', text: `¡Propietario "${newOwner.name}" agregado!` });
        setEditingOwner(null);
        setActiveTab('all-dogs');
    };
    const handleOwnerFormCancel = () => { setEditingOwner(null); setActiveTab('all-dogs'); };

    // ── Desktop computed ─────────────────────────────────────────────────────
    const uniqueBreeds = [...new Set(dogs.map(d => d.breed).filter(Boolean))].sort();

    const filteredDogs = dogs;

    const overviewFiltered = dogs;

    const recentCount = dogs.filter(d => {
        if (!d.createdAt) return false;
        const ago = new Date(); ago.setDate(ago.getDate() - 7);
        return new Date(d.createdAt) >= ago;
    }).length;
    const breedsCount = uniqueBreeds.length;

    // ── Mobile computed ──────────────────────────────────────────────────────
    const mobileDogs = dogs;

    const isMobileForm = activeTab === 'dog-form' || activeTab === 'add-owner';

    // ────────────────────────────────────────────────────────────────────────
    return (
        <div>
            <Navigation />

            {/* ════════════ MOBILE VIEW ════════════ */}
            <div className="dogs-mobile-view">

                {isMobileForm ? (
                    /* ── Form page (full-screen on mobile) ── */
                    <div className="dmv-form-page">
                        <div className="dmv-form-header">
                            <button className="dmv-form-back" onClick={handleFormCancel}>‹</button>
                            <h2 className="dmv-form-title">
                                {activeTab === 'add-owner'
                                    ? (editingOwner ? 'Editar propietario' : 'Nuevo propietario')
                                    : (editingDog   ? 'Editar perro'   : 'Nuevo perro')}
                            </h2>
                            <div style={{ width: 38 }} />
                        </div>
                        {activeTab === 'add-owner' ? (
                            <OwnerForm
                                onSubmit={handleOwnerFormSubmit}
                                onCancel={handleOwnerFormCancel}
                                loading={loading}
                                editingOwner={editingOwner}
                            />
                        ) : (
                            <DogForm
                                onSubmit={handleFormSubmit}
                                onCancel={handleFormCancel}
                                loading={loading}
                                editingDog={editingDog}
                            />
                        )}
                    </div>
                ) : (
                    /* ── List view ── */
                    <>
                        {/* Header row */}
                        <div className="dmv-header">
                            <h1 className="dmv-title">Perros</h1>
                            <button
                                className="dmv-add-btn"
                                onClick={() => { setEditingDog(null); setActiveTab('dog-form'); }}
                            >
                                + Agregar
                            </button>
                        </div>

                        {/* Search bar */}
                        <div className="dmv-search">
                            <span className="dmv-search-ico">🔍</span>
                            <input
                                className="dmv-search-input"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                placeholder="Buscar perros, raza, propietario…"
                            />
                            {searchTerm && (
                                <button className="dmv-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                            )}
                        </div>

                        {/* Filter chips */}
                        <div className="dmv-chips">
                            {CHIPS.map(({ id, label }) => (
                                <button
                                    key={id}
                                    className={`dmv-chip ${mobileFilter === id ? 'on' : ''}`}
                                    onClick={() => setMobileFilter(id)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Status message */}
                        {message.text && (
                            <div className={`dmv-msg dmv-msg--${message.type}`}>{message.text}</div>
                        )}

                        {/* Dog list */}
                        {loading ? (
                            <div className="dmv-empty">Cargando perros…</div>
                        ) : mobileDogs.length === 0 ? (
                            <div className="dmv-empty">
                                {searchTerm || mobileFilter !== 'all'
                                    ? 'Ningún perro coincide con tu búsqueda.'
                                    : 'No hay perros registrados. Toca + Agregar para empezar.'}
                            </div>
                        ) : (
                            <div className="dmv-list">
                                {mobileDogs.map(dog => {
                                    const { bg, emoji } = getDogStyle(dog.breed);
                                    const owner    = dog.owners?.[0];
                                    const ageShort = getAgeShort(dog);
                                    const meta     = [dog.breed, ageShort, owner?.name].filter(Boolean).join(' · ');
                                    const tag      = (dog.breed || 'DOG').substring(0, 7).toUpperCase();
                                    return (
                                        <div
                                            key={dog._id}
                                            className="dmv-card"
                                            onClick={() => handleViewDetails(dog)}
                                        >
                                            <div className="dmv-avatar" style={{ background: bg }}>
                                                {emoji}
                                            </div>
                                            <div className="dmv-info">
                                                <div className="dmv-name">{dog.name || 'Sin nombre'}</div>
                                                <div className="dmv-meta">{meta}</div>
                                            </div>
                                            <div className="dmv-right">
                                                {dog.status
                                                    ? <StatusPill status={dog.status} />
                                                    : <span className="dmv-tag" style={{ background: bg }}>{tag}</span>
                                                }
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {!loading && (
                            <PaginationControls page={page} totalPages={totalPages} setPage={setPage} />
                        )}
                    </>
                )}
            </div>

            {/* ════════════ DESKTOP VIEW ════════════ */}
            <div className="dogs-container">
                <div className="dogs-header">
                    <h1>Perros</h1>
                </div>

                {(message.text || error) && (
                    <div className={`message ${message.type || 'error'}`}>{message.text || error}</div>
                )}

                <div className="tab-navigation">
                    <button onClick={() => { setActiveTab('overview');  setEditingDog(null); }} className={`tab-button ${activeTab === 'overview'  ? 'active' : ''}`}>📋 Resumen</button>
                    <button onClick={() => { setActiveTab('dog-form');  if (!editingDog) setEditingDog(null); }} className={`tab-button ${activeTab === 'dog-form'  ? 'active' : ''}`}>{editingDog ? '✏️ Editar perro' : '➕ Agregar perro'}</button>
                    <button onClick={() => { setActiveTab('all-dogs'); setEditingDog(null); }} className={`tab-button ${activeTab === 'all-dogs' ? 'active' : ''}`}>🐾 Todos los perros</button>
                    <button onClick={() => navigate('/owners')} className="tab-button">👥 Ver propietarios</button>
                    <button onClick={() => { setActiveTab('add-owner'); setEditingDog(null); setEditingOwner(null); }} className={`tab-button ${activeTab === 'add-owner' ? 'active' : ''}`}>{editingOwner ? '✏️ Editar propietario' : '👤 Agregar propietario'}</button>
                </div>

                {/* Overview */}
                {activeTab === 'overview' && (
                    <div className="overview-tab">
                        <div className="quick-stats">
                            <div className="stat-card"><h3>🐕 Total perros</h3><p className="stat-number">{totalDogs || dogs.length}</p><p className="stat-label">perro{(totalDogs || dogs.length) !== 1 ? 's' : ''} registrado{(totalDogs || dogs.length) !== 1 ? 's' : ''}</p></div>
                            <div className="stat-card"><h3>🆕 Recientes</h3><p className="stat-number">{recentCount}</p><p className="stat-label">agregados esta semana</p></div>
                            <div className="stat-card"><h3>🐕‍🦺 Razas</h3><p className="stat-number">{breedsCount}</p><p className="stat-label">raza{breedsCount !== 1 ? 's' : ''} diferente{breedsCount !== 1 ? 's' : ''}</p></div>
                        </div>
                        <div className="overview-search-section">
                            <div className="search-bar">
                                <div className="search-input-wrapper">
                                    <span className="search-icon">🔍</span>
                                    <input type="text" value={overviewSearch} onChange={e => setOverviewSearch(e.target.value)} placeholder="Buscar perros por nombre, raza o propietario..." className="search-input" />
                                    {overviewSearch && <button onClick={() => setOverviewSearch('')} className="clear-search-btn">✕</button>}
                                </div>
                                {overviewSearch && <div className="search-results-info"><span>{overviewFiltered.length} de {totalDogs || dogs.length} perros encontrados</span></div>}
                            </div>
                        </div>
                        <DogsDisplay dogs={overviewFiltered} loading={loading} onEdit={handleEdit} onDelete={handleDelete} onViewDetails={handleViewDetails} />
                        {!loading && (
                            <PaginationControls page={page} totalPages={totalPages} setPage={setPage} />
                        )}
                    </div>
                )}

                {/* Dog Form */}
                {activeTab === 'dog-form' && (
                    <div className="dog-form-tab">
                        <DogForm onSubmit={handleFormSubmit} onCancel={handleFormCancel} loading={loading} editingDog={editingDog} />
                    </div>
                )}

                {/* All Dogs */}
                {activeTab === 'all-dogs' && (
                    <div className="all-dogs-tab">
                        <div className="dogs-header-section">
                            <h2>Todos los perros</h2>
                            <div className="dogs-filters">
                                <div className="filter-group">
                                    <label>Buscar</label>
                                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por nombre, raza o propietario..." />
                                </div>
                                <div className="filter-group">
                                    <label>Raza</label>
                                    <select value={breedFilter} onChange={e => setBreedFilter(e.target.value)}>
                                        <option value="">Todas las razas</option>
                                        {uniqueBreeds.map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                </div>
                                {(searchTerm || breedFilter) && (
                                    <button onClick={() => { setSearchTerm(''); setBreedFilter(''); }} className="clear-filters-btn">Limpiar filtros</button>
                                )}
                            </div>
                        </div>
                        {loading ? (
                            <div className="loading">Cargando todos los perros...</div>
                        ) : filteredDogs.length === 0 ? (
                            <div className="no-dogs">{searchTerm || breedFilter ? <p>Ningún perro coincide con tu búsqueda.</p> : <p>No hay perros registrados aún.</p>}</div>
                        ) : (
                            <div className="dogs-grid">
                                {filteredDogs.map(dog => (
                                    <DogCard key={dog._id} dog={dog} onEdit={handleEdit} onDelete={handleDelete} onViewDetails={handleViewDetails} />
                                ))}
                            </div>
                        )}
                        {!loading && (
                            <PaginationControls page={page} totalPages={totalPages} setPage={setPage} />
                        )}
                    </div>
                )}

                {/* Add Owner */}
                {activeTab === 'add-owner' && (
                    <div className="add-owner-tab">
                        <OwnerForm onSubmit={handleOwnerFormSubmit} onCancel={handleOwnerFormCancel} loading={loading} editingOwner={editingOwner} />
                    </div>
                )}
            </div>

            {/* Dog Details Modal (both views) */}
            <DogDetailsModal
                dog={selectedDog}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onEdit={handleEdit}
                onDelete={(dogId) => { handleCloseModal(); handleDelete(dogId); }}
                onDogUpdated={(updatedDog) => { patchDog(updatedDog); setSelectedDog(updatedDog); }}
            />
        </div>
    );
};

export default Dogs;
